/**
 * Adapted from siteswap-js at commit fe62676709ff3a1444fca076a70d03ea2bbcd915.
 *
 * Copyright 2021 Tom Whitfield
 *
 * Permission to use, copy, modify, and/or distribute this software for any purpose with or
 * without fee is hereby granted, provided that the above copyright notice and this permission
 * notice appear in all copies.
 *
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH REGARD TO THIS
 * SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE
 * AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
 * WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT,
 * NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR PERFORMANCE
 * OF THIS SOFTWARE.
 */

export type JugglingProp = 'b' | 'c' | 'r';
export type JugglingHand = 'left' | 'right';

export type SiteswapThrow = Readonly<{ value: number }>;
export type SiteswapPattern = Readonly<{
  canonical: string;
  synchronous: boolean;
  period: number;
  propCount: number;
  maximumThrow: number;
  left: readonly (readonly SiteswapThrow[])[];
  right: readonly (readonly SiteswapThrow[])[];
}>;

type ParsedBeat = { left: number[]; right: number[] };

export class SiteswapError extends Error {
  override readonly name = 'SiteswapError';
}

export function parseSiteswap(input: string): SiteswapPattern {
  if (typeof input !== 'string' || input.length === 0 || /\s/.test(input)) {
    throw new SiteswapError('Invalid siteswap. Use compact notation without spaces.');
  }
  let source = input.toLowerCase().replaceAll('2t', '2');
  if (!/^[a-z0-9()[\],*]+$/.test(source)) throw new SiteswapError('Invalid siteswap.');

  const synchronous = source.startsWith('(');
  let beats: ParsedBeat[];
  if (synchronous) {
    const parsed = parseSynchronous(source);
    source = parsed.canonical;
    beats = parsed.beats;
  } else {
    if (
      source.includes('(') ||
      source.includes(')') ||
      source.includes(',') ||
      source.includes('*')
    ) {
      throw new SiteswapError('Invalid siteswap.');
    }
    beats = parseAsynchronous(source);
    if (beats.length % 2 === 1) beats = [...beats, ...beats.map(mirroredBeat)];
  }
  if (beats.length === 0) throw new SiteswapError('Invalid siteswap.');

  const sum = beats.reduce(
    (total, beat) =>
      total +
      beat.left.reduce((value, throwValue) => value + Math.abs(throwValue), 0) +
      beat.right.reduce((value, throwValue) => value + Math.abs(throwValue), 0),
    0,
  );
  const propCount = sum / beats.length;
  if (!Number.isInteger(propCount)) throw new SiteswapError('Siteswap average is not an integer.');
  validateLandings(beats);
  const maximumThrow = beats.reduce(
    (maximum, beat) => Math.max(maximum, ...beat.left.map(Math.abs), ...beat.right.map(Math.abs)),
    0,
  );
  return {
    canonical: source,
    synchronous,
    period: beats.length,
    propCount,
    maximumThrow,
    left: beats.map((beat) => beat.left.map((value) => ({ value }))),
    right: beats.map((beat) => beat.right.map((value) => ({ value }))),
  };
}

function parseAsynchronous(source: string): ParsedBeat[] {
  const beats: ParsedBeat[] = [];
  let hand: JugglingHand = 'left';
  for (let index = 0; index < source.length;) {
    let values: number[];
    if (source[index] === '[') {
      const closing = source.indexOf(']', index + 1);
      if (closing < 0 || source.slice(index + 1, closing).length < 2) {
        throw new SiteswapError('Invalid multiplex.');
      }
      const body = source.slice(index + 1, closing);
      if (!/^[a-z0-9]{2,}$/.test(body)) throw new SiteswapError('Invalid multiplex.');
      values = Array.from(body, throwValue);
      index = closing + 1;
    } else {
      if (source[index] === ']') throw new SiteswapError('Invalid multiplex.');
      values = [throwValue(requiredCharacter(source[index]))];
      index += 1;
    }
    beats.push(hand === 'left' ? { left: values, right: [0] } : { left: [0], right: values });
    hand = hand === 'left' ? 'right' : 'left';
  }
  return beats;
}

function parseSynchronous(source: string): { canonical: string; beats: ParsedBeat[] } {
  const mirrored = source.endsWith('*');
  const body = mirrored ? source.slice(0, -1) : source;
  if (body.includes('*')) throw new SiteswapError('Invalid synchronous siteswap.');
  const pairs: { left: number[]; right: number[]; textLeft: string; textRight: string }[] = [];
  let index = 0;
  while (index < body.length) {
    if (body[index] !== '(') throw new SiteswapError('Invalid synchronous siteswap.');
    const comma = body.indexOf(',', index + 1);
    const closing = body.indexOf(')', index + 1);
    if (
      comma < 0 ||
      closing < 0 ||
      comma > closing ||
      body.slice(index + 1, closing).includes('(')
    ) {
      throw new SiteswapError('Invalid synchronous siteswap.');
    }
    const textLeft = body.slice(index + 1, comma);
    const textRight = body.slice(comma + 1, closing);
    pairs.push({
      left: parseSynchronousThrow(textLeft),
      right: parseSynchronousThrow(textRight),
      textLeft,
      textRight,
    });
    index = closing + 1;
  }
  if (pairs.length === 0) throw new SiteswapError('Invalid synchronous siteswap.');
  const expanded = mirrored
    ? [
        ...pairs,
        ...pairs.map((pair) => ({
          ...pair,
          left: pair.right,
          right: pair.left,
          textLeft: pair.textRight,
          textRight: pair.textLeft,
        })),
      ]
    : pairs;
  return {
    canonical: expanded.map((pair) => `(${pair.textLeft},${pair.textRight})`).join(''),
    beats: expanded.flatMap((pair) => [
      { left: pair.left, right: pair.right },
      { left: [0], right: [0] },
    ]),
  };
}

function parseSynchronousThrow(text: string): number[] {
  if (text.startsWith('[') && text.endsWith(']')) {
    const body = text.slice(1, -1);
    if (body.length < 2) throw new SiteswapError('Invalid synchronous multiplex.');
    const result: number[] = [];
    for (let index = 0; index < body.length;) {
      const parsed = synchronousValue(body, index);
      result.push(parsed.value);
      index = parsed.next;
    }
    if (result.length < 2) throw new SiteswapError('Invalid synchronous multiplex.');
    return result;
  }
  if (text.includes('[') || text.includes(']'))
    throw new SiteswapError('Invalid synchronous multiplex.');
  const parsed = synchronousValue(text, 0);
  if (parsed.next !== text.length) throw new SiteswapError('Invalid synchronous throw.');
  return [parsed.value];
}

function synchronousValue(text: string, index: number): { value: number; next: number } {
  const character = requiredCharacter(text[index]);
  const value = throwValue(character);
  if (value % 2 !== 0) throw new SiteswapError('Synchronous throws must have even values.');
  const crossing = text[index + 1] === 'x';
  return { value: crossing ? -value : value, next: index + (crossing ? 2 : 1) };
}

function throwValue(character: string): number {
  if (/^[0-9]$/.test(character)) return Number(character);
  if (/^[a-z]$/.test(character)) return character.charCodeAt(0) - 87;
  throw new SiteswapError('Invalid throw value.');
}

function validateLandings(beats: readonly ParsedBeat[]): void {
  const incomingLeft = Array.from({ length: beats.length }, () => 0);
  const incomingRight = Array.from({ length: beats.length }, () => 0);
  const outgoingLeft = beats.map((beat) => (beat.left[0] === 0 ? 0 : beat.left.length));
  const outgoingRight = beats.map((beat) => (beat.right[0] === 0 ? 0 : beat.right.length));
  beats.forEach((beat, index) => {
    for (const value of beat.left) {
      if (value === 0) continue;
      const target = (index + Math.abs(value)) % beats.length;
      if (value > 0 && value % 2 === 0) {
        incomingLeft[target] = (incomingLeft[target] ?? 0) + 1;
      } else {
        incomingRight[target] = (incomingRight[target] ?? 0) + 1;
      }
    }
    for (const value of beat.right) {
      if (value === 0) continue;
      const target = (index + Math.abs(value)) % beats.length;
      if (value > 0 && value % 2 === 0) {
        incomingRight[target] = (incomingRight[target] ?? 0) + 1;
      } else {
        incomingLeft[target] = (incomingLeft[target] ?? 0) + 1;
      }
    }
  });
  if (
    beats.some(
      (_, index) =>
        incomingLeft[index] !== outgoingLeft[index] ||
        incomingRight[index] !== outgoingRight[index],
    )
  ) {
    throw new SiteswapError('Siteswap throws collide on landing.');
  }
}

function mirroredBeat(beat: ParsedBeat): ParsedBeat {
  return { left: [...beat.right], right: [...beat.left] };
}

function requiredCharacter(value: string | undefined): string {
  if (value === undefined) throw new SiteswapError('Missing throw value.');
  return value;
}
